/**
 * Library Posts — sibling to Library Videos. Three filter pills:
 * Drafts / Scheduled / Published. Drafts are pre-publish queue items
 * requiring operator approval; Scheduled+Published source from Buffer
 * via the existing /api/scheduling/posts endpoint (future milestone).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ImageIcon, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useIsMobile } from "../lib/useIsMobile";
import {
  extractTextFromImage,
  generateBetaPostDraft,
  generatePostDraft,
  getSettings,
  listBetaPostDrafts,
  listPostDrafts,
  type BetaPostDraft,
  type BetaPostDraftStatus,
  type BetaSeedMode,
  type PostDraft,
  type PostDraftStatus,
} from "../api";
import { PageHeader } from "../components/PageHeader";
import { PostCard } from "../components/PostCard";
import { BetaPostCard } from "../components/BetaPostCard";
import { LibraryDateControls } from "../components/LibraryDateControls";
import {
  applyDateView,
  hasDateRange,
  readSortPref,
  SORT_PREF_KEYS,
  writeSortPref,
  type DateRange,
  type SortOrder,
} from "../lib/librarySort";

type Tab = "all" | "drafts" | "scheduled" | "published";
type Pipeline = "all" | "beta" | "classic";

/** Statuses that mean "work is happening on the server right now". The
 *  screen polls while any draft is in one of these, so the operator
 *  doesn't have to manually refresh to see the spinner clear. */
const TRANSIENT_BETA_STATUSES: BetaPostDraftStatus[] = ["generating-text", "rendering"];

export function LibraryPosts(): React.JSX.Element {
  const isMobile = useIsMobile();
  // Default to All so newly published / in-flight posts are visible
  // without clicking through filters.
  const [tab, setTab] = useState<Tab>("all");
  // PIPELINE view filter. Default to "all" so both AI-image and Legacy
  // posts are visible the moment the page opens (operator request
  // 2026-06-23). AI-image stays the primary workflow; classic is Legacy.
  const [pipeline, setPipeline] = useState<Pipeline>("all");
  // What the Generate button produces — decoupled from the view filter
  // above so the "All" view is no longer hardcoded to AI-image. The
  // segmented toggle next to Generate sets this; selecting a single-pipeline
  // VIEW tab syncs it (viewing Legacy ⇒ generating Legacy). See selectPipeline.
  const [genPipeline, setGenPipeline] = useState<"beta" | "classic">("beta");
  const [drafts, setDrafts] = useState<PostDraft[]>([]);
  const [betaDrafts, setBetaDrafts] = useState<BetaPostDraft[]>([]);
  // Sort + date range (2026-09-14). The order is a remembered per-page
  // preference so "Oldest" sticks once chosen — the operator opens this page
  // to find posts to RECYCLE, and the oldest are the candidates. The range is
  // session-only: a persisted range would silently hide posts next visit.
  const [order, setOrderState] = useState<SortOrder>(() => readSortPref(SORT_PREF_KEYS.posts));
  const setOrder = (o: SortOrder): void => {
    setOrderState(o);
    writeSortPref(SORT_PREF_KEYS.posts, o);
  };
  const [range, setRange] = useState<DateRange>({});
  // "Schedule again": the copy the server just made. Its card opens the
  // Publish modal on mount, and the id is PINNED so a background refetch on
  // the Published tab can't drop the pending copy (and close its modal) while
  // the operator is still picking a time. Unpinned once the copy leaves its
  // pre-publish status (scheduled / published / discarded).
  const [autoPublishId, setAutoPublishId] = useState<string | null>(null);
  // Per pipeline: each fetch prunes against ITS OWN rows, so a Legacy pin
  // must never be judged "gone" by the Beta fetch (they run in parallel on
  // the All pipeline).
  const pinnedRef = useRef<{ classic: Set<string>; beta: Set<string> }>({ classic: new Set(), beta: new Set() });
  // Server-authoritative feature flag (QL_POST_LENGTH_CONTROL) for the cards'
  // Shorter/Longer buttons. Fetched once here rather than per card, and
  // defaults to hidden so an older server or a failed fetch never renders a
  // button whose route would 404.
  const [lengthControl, setLengthControl] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seed, setSeed] = useState("");
  // Faithful = keep operator's wording (~5% change max); Reinvent =
  // distil to felt center (legacy behaviour). The toggle only appears
  // when the seed looks like content rather than a short theme tag.
  const [seedMode, setSeedMode] = useState<BetaSeedMode>("faithful");
  // OCR state for the image-extraction shortcut on the seed field.
  // `extracting` gates the button + textarea while Claude vision runs;
  // `extractError` surfaces failures inline so the operator doesn't
  // need to dig through console.
  const [extracting, setExtracting] = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleExtractFromImage = useCallback(async (file: File): Promise<void> => {
    if (extracting) return;
    setExtractError(null);
    setExtracting(true);
    try {
      const text = await extractTextFromImage(file);
      if (!text) {
        setExtractError("No text detected in that image.");
        return;
      }
      // Replace any current seed — the operator's flow is "drop an
      // image, generate post for THAT quote", so silently appending
      // would surprise them. They can always paste extra context
      // afterwards if needed.
      setSeed(text);
    } catch (err) {
      setExtractError(err instanceof Error ? err.message : String(err));
    } finally {
      setExtracting(false);
    }
  }, [extracting]);

  // Cmd+V paste-image on the textarea. We intercept BEFORE the textarea
  // gets the paste so the binary image data doesn't get stringified
  // into the field as garbled text.
  const handleSeedPaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>): Promise<void> => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      if (item.kind === "file" && item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) void handleExtractFromImage(file);
        return;
      }
    }
    // No image — let the default paste behaviour run as text.
  }, [handleExtractFromImage]);

  // `silent` skips the page-level loading flicker. Background polls and
  // per-card refresh callbacks must run silent — otherwise the conditional
  // `{loading ? <Loading/> : <Grid/>}` unmounts the entire grid every
  // 3 seconds while a draft is mid-render, wiping every open Edit modal,
  // History panel, and unsaved textarea on OTHER cards. Initial mount and
  // tab/pipeline switches still flip loading on so the user sees feedback
  // when navigating to a fresh data set.
  const fetchDrafts = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    setError(null);
    // Classic status filter (single status or undefined for "all" tab).
    const statusForTab: Record<Tab, PostDraftStatus | undefined> = {
      all: undefined,
      drafts: "pending",
      scheduled: "scheduled",
      published: "published",
    };
    // Beta: the Drafts tab is anything pre-publish PLUS failed drafts (so the
    // operator can triage + Retry them from the queue, not just from "All").
    // The All tab shows every status (no filter).
    const statusFor: Record<Tab, BetaPostDraftStatus[] | undefined> = {
      all: undefined,
      drafts: ["generating-text", "awaiting-image", "rendering", "ready-for-review", "generation-failed"],
      scheduled: ["scheduled"],
      published: ["published"],
    };
    // Legacy draft-ish statuses shown in the Drafts tab — pending PLUS failed,
    // so a stuck card is visible where the operator looks to fix it.
    const CLASSIC_DRAFT_STATUSES: PostDraftStatus[] = ["pending", "generation-failed", "render-failed"];
    // A recycled copy stays pinned to the current tab only while it is still
    // waiting to be scheduled; once it moves on (or is discarded) the pin is
    // dropped and the tab filter applies normally again.
    const prune = (pinned: Set<string>, rows: Array<{ id: string; status: string }>): void => {
      for (const id of [...pinned]) {
        const d = rows.find((r) => r.id === id);
        if (!d || (d.status !== "pending" && d.status !== "ready-for-review")) pinned.delete(id);
      }
    };
    const fetchClassic = async (): Promise<void> => {
      const pinned = pinnedRef.current.classic;
      if (tab === "drafts") {
        const all = await listPostDrafts();
        prune(pinned, all);
        setDrafts(all.filter((d) => CLASSIC_DRAFT_STATUSES.includes(d.status) || pinned.has(d.id)));
      } else if (tab === "all" || pinned.size === 0) {
        const rows = await listPostDrafts(statusForTab[tab]);
        if (tab === "all") prune(pinned, rows);
        setDrafts(rows);
      } else {
        // A pin is live on a status-filtered tab: pull everything so the
        // pinned copy can ride along with the tab's own rows.
        const all = await listPostDrafts();
        prune(pinned, all);
        setDrafts(all.filter((d) => d.status === statusForTab[tab] || pinned.has(d.id)));
      }
    };
    const fetchBeta = async (): Promise<void> => {
      const pinned = pinnedRef.current.beta;
      const want = statusFor[tab];
      const all = await listBetaPostDrafts();
      prune(pinned, all);
      setBetaDrafts(want ? all.filter((d) => want.includes(d.status) || pinned.has(d.id)) : all);
    };
    try {
      if (pipeline === "classic") {
        await fetchClassic();
      } else if (pipeline === "beta") {
        await fetchBeta();
      } else {
        // All: pull both pipelines (still respecting the selected tab)
        // so the merged grid sees the full picture.
        await Promise.all([fetchClassic(), fetchBeta()]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [tab, pipeline]);

  useEffect(() => {
    void fetchDrafts();
  }, [fetchDrafts]);

  // One settings read for the whole page — the cards get the flag as a prop
  // instead of each fetching it. A failure leaves the buttons hidden, which
  // is the safe direction.
  useEffect(() => {
    let cancelled = false;
    void getSettings()
      .then((s) => {
        if (!cancelled) setLengthControl(s.features?.postLengthControl === true);
      })
      .catch(() => { /* keep the buttons hidden */ });
    return () => { cancelled = true; };
  }, []);

  // Auto-refresh while anything is mid-flight. Polls every 3 s and
  // self-stops as soon as no draft is in a transient status — so an
  // idle library page doesn't burn requests. Re-runs whenever the
  // visible draft set changes (a new render kicks polling back on).
  //
  // The "wasActive" ref + the trailing fetch below catches a subtle
  // race: when Claude finishes mid-poll, the poll that observes the
  // status flip can race the server's file-rewrite and read partial
  // state. By firing ONE MORE fetch ~800 ms after the transition
  // out of transient, we guarantee the user sees the fully-written
  // final state even if the deciding poll caught the file mid-write.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    // Poll while ANY visible draft is in flight. Beta: transient statuses +
    // the background quote-length marker. Legacy (2026-08-19): the
    // "generating-text" stub + any background busy marker (regenerate /
    // quote-length) — these actions now run server-side and the card learns
    // their outcome only by polling, so this loop is what makes an action
    // survive the operator navigating away and coming back.
    const betaActive =
      (pipeline === "beta" || pipeline === "all") &&
      betaDrafts.some((d) => TRANSIENT_BETA_STATUSES.includes(d.status) || !!d.busy);
    const legacyActive =
      (pipeline === "classic" || pipeline === "all") &&
      drafts.some((d) => d.status === "generating-text" || !!d.busy);
    const active = betaActive || legacyActive;
    if (!active) {
      // Transition transient → settled: schedule a settle fetch.
      if (wasActiveRef.current) {
        wasActiveRef.current = false;
        const settle = window.setTimeout(() => { void fetchDrafts({ silent: true }); }, 800);
        return () => window.clearTimeout(settle);
      }
      return;
    }
    wasActiveRef.current = true;
    const t = setInterval(() => { void fetchDrafts({ silent: true }); }, 3000);
    return () => clearInterval(t);
  }, [pipeline, betaDrafts, drafts, fetchDrafts]);

  const handleGenerate = async (): Promise<void> => {
    // Both pipelines now answer as soon as the server-side stub exists
    // (2026-08-19 — Legacy got the same two-phase model Beta always had), so
    // `generating` only spans the sub-second POST. The input frees
    // immediately and the operator can queue the next post while this one
    // writes itself in the background; the stub card arrives on the silent
    // refresh below and polling carries it to done.
    setGenerating(true);
    setError(null);
    try {
      if (genPipeline === "classic") {
        // Classic now supports the same seed input as Beta: a short seed is
        // the theme; a quote/paragraph switches to faithful/reinvent.
        const trimmedSeed = seed.trim();
        const isContentSeed = trimmedSeed.length >= 80 || /[.!?؟،"“”«»]/.test(trimmedSeed);
        await generatePostDraft(
          trimmedSeed || undefined,
          isContentSeed ? { seedMode } : {},
        );
        if (trimmedSeed) setSeed("");
      } else {
        // Seed text is optional. When present it's used verbatim as the
        // theme; when blank the backend picks from the rotation pool.
        const trimmedSeed = seed.trim();
        // seedMode only matters when the seed reads as content (paragraph
        // or quote). The server ignores it for short theme tags via
        // effectiveSeedMode(), but we still scope the option here so the
        // outgoing payload reflects the operator's intent.
        const isContentSeed = trimmedSeed.length >= 80 || /[.!?؟،"“”«»]/.test(trimmedSeed);
        await generateBetaPostDraft(
          trimmedSeed || undefined,
          isContentSeed ? { seedMode } : {},
        );
        if (trimmedSeed) setSeed("");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
    // Silent refresh AFTER the input is already free — brings in the stub
    // card without wiping Edit/History state on any sibling card.
    await fetchDrafts({ silent: true });
  };

  // Mirror the server's `looksLikeContentSeed` heuristic so the toggle
  // only shows up when faithful-vs-reinvent is a meaningful choice.
  // For short theme tags ("patience", "the cost of clarity"), there's
  // no operator wording to preserve, so the toggle is hidden.
  const trimmedSeed = seed.trim();
  const showSeedModeToggle =
    trimmedSeed.length >= 80 || /[.!?؟،"“”«»]/.test(trimmedSeed);

  // Which pipeline the Generate button produces, surfaced on the button so the
  // operator never has to guess. Driven by the dedicated genPipeline toggle —
  // NOT the view filter — so "All" can generate either type.
  const genTarget = genPipeline === "classic" ? "Legacy" : "AI image";

  // Selecting a VIEW tab. Picking a single pipeline ("AI image"/"Legacy") also
  // points Generate at it (viewing Legacy ⇒ generating Legacy — the intuitive
  // default). Picking "All" leaves the Generate target on its last choice.
  const selectPipeline = (id: Pipeline): void => {
    setPipeline(id);
    if (id !== "all") setGenPipeline(id);
  };

  // Flipping the Generate-target toggle. If we're focused on the OTHER single
  // pipeline's view, widen to "All" so the freshly generated post is visible
  // (otherwise it'd be filtered out of the current view and look like nothing
  // happened).
  const selectGenPipeline = (id: "beta" | "classic"): void => {
    setGenPipeline(id);
    setPipeline((p) => (p !== "all" && p !== id ? "all" : p));
  };
  const isQuoteSeed = trimmedSeed.length > 80 || /[.!?؟،]/.test(trimmedSeed);

  // The Legacy generate is synchronous (no server stub like Beta), so without
  // this the grid shows nothing until the post is fully rendered. Show an
  // optimistic skeleton card the instant Generate is clicked while targeting
  // Legacy — the same "Writing the post…" feel as the Beta stub. Rendered in
  // both the Legacy and All grids below. (Beta surfaces its own server-side
  // stub via polling.)
  const showClassicSkeleton = generating && genPipeline === "classic";

  // Sort + date range applied to EVERY grid (2026-09-14). Date axis = the
  // date the card shows (generatedAt). Before this the single-pipeline grids
  // rendered in server order and only the "All" grid was sorted.
  const viewDrafts = useMemo(
    () => applyDateView(drafts, (d) => d.generatedAt, order, range),
    [drafts, order, range],
  );
  const viewBeta = useMemo(
    () => applyDateView(betaDrafts, (d) => d.generatedAt, order, range),
    [betaDrafts, order, range],
  );
  // "All" pipeline: interleave both card types into one grid. Each row is
  // tagged so the renderer knows which card component to use. null for the
  // single-pipeline modes.
  const merged = useMemo(
    () =>
      pipeline === "all"
        ? applyDateView(
            [
              ...drafts.map((d) => ({ kind: "classic" as const, d })),
              ...betaDrafts.map((d) => ({ kind: "beta" as const, d })),
            ],
            (row) => row.d.generatedAt,
            order,
            range,
          )
        : null,
    [pipeline, drafts, betaDrafts, order, range],
  );
  const rawCount =
    pipeline === "all" ? drafts.length + betaDrafts.length
    : pipeline === "classic" ? drafts.length
    : betaDrafts.length;
  const viewCount =
    pipeline === "all" ? (merged?.length ?? 0)
    : pipeline === "classic" ? viewDrafts.length
    : viewBeta.length;

  // "Schedule again" landed a copy: show it at once (no refetch round-trip),
  // pin it to this tab, and let its card open the scheduler on mount.
  const handleRecycledClassic = (copy: PostDraft): void => {
    pinnedRef.current.classic.add(copy.id);
    setAutoPublishId(copy.id);
    setDrafts((prev) => [copy, ...prev.filter((d) => d.id !== copy.id)]);
  };
  const handleRecycledBeta = (copy: BetaPostDraft): void => {
    pinnedRef.current.beta.add(copy.id);
    setAutoPublishId(copy.id);
    setBetaDrafts((prev) => [copy, ...prev.filter((d) => d.id !== copy.id)]);
  };

  // Legacy skeleton card — used for the sub-second optimistic window while
  // the generate POST is in flight, AND for every server-persisted
  // "generating-text" stub (2026-08-19): the stub lives on disk, so this
  // skeleton survives refresh and navigation where the old client-only one
  // died. Card chrome matches the real PostCard (radius 18 / padding 18 /
  // 4:5 image) so it doesn't jump when the finished card swaps in.
  const classicSkeleton = (key: string): React.JSX.Element => (
    <div
      key={key}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        padding: 18,
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,.06)",
        background: "rgba(16,18,27,.72)",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          aspectRatio: "4 / 5",
          borderRadius: 14,
          overflow: "hidden",
          background:
            "radial-gradient(ellipse at center, rgba(255,213,0,.10) 0%, rgba(0,0,0,0) 65%), rgba(0,0,0,.35)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 14,
        }}
      >
        <Loader2 size={34} className="ql-spin" color="var(--ql-yellow)" />
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: "var(--ql-yellow)",
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          Writing the post…
        </div>
        <div
          style={{
            fontSize: 11,
            color: "var(--ql-fg-muted)",
            textAlign: "center",
            lineHeight: 1.5,
            maxWidth: 240,
            padding: "0 16px",
          }}
        >
          Claude is drafting the Arabic + English slides. The card fills in automatically.
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ height: 12, width: "60%", borderRadius: 6, background: "rgba(255,255,255,.08)" }} />
        <div style={{ height: 9, width: "35%", borderRadius: 6, background: "rgba(255,255,255,.05)" }} />
      </div>
    </div>
  );
  const classicSkeletonCard = classicSkeleton("classic-skeleton");

  return (
    <div>
      <PageHeader
        eyebrow="Library · Posts"
        title="Every Quotologies post — drafts and shipped."
        subtitle="The autonomous post pipeline generates twice a day. Approve drafts here to schedule them to Buffer."
        action={
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {(
              <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: "1 1 320px", minWidth: 240, maxWidth: 520 }}>
                <div style={{ position: "relative" }}>
                  <textarea
                    value={seed}
                    onChange={(e) => setSeed(e.target.value)}
                    onPaste={handleSeedPaste}
                    onKeyDown={(e) => {
                      // Cmd/Ctrl-Enter fires generate; plain Enter inserts
                      // a newline (so pasting a multi-paragraph quote
                      // doesn't accidentally submit half of it).
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && !generating) {
                        e.preventDefault();
                        void handleGenerate();
                      }
                    }}
                    placeholder={
                      extracting
                        ? "Extracting text from image…"
                        : "Optional seed — type a theme, paste a quote, or drop an image (camera icon · ⌘+V also works). ⌘+Enter to generate."
                    }
                    disabled={generating || extracting}
                    rows={2}
                    style={{
                      width: "100%",
                      // Extra right padding so the camera button doesn't
                      // sit over the first line of typed text.
                      padding: "10px 44px 10px 14px",
                      borderRadius: 12,
                      border: "1px solid rgba(255,255,255,.10)",
                      background: "rgba(255,255,255,.04)",
                      color: "var(--ql-fg)",
                      fontSize: 13,
                      fontFamily: "inherit",
                      outline: "none",
                      resize: "vertical",
                      lineHeight: 1.5,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={generating || extracting}
                    title="Extract text from an image (also: ⌘+V to paste an image directly)"
                    aria-label="Extract text from image"
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      width: 28,
                      height: 28,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,.10)",
                      background: extracting ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.04)",
                      color: extracting ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                      cursor: generating || extracting ? "wait" : "pointer",
                      padding: 0,
                    }}
                  >
                    {extracting ? (
                      <Loader2 size={14} className="ql-spin" />
                    ) : (
                      <ImageIcon size={14} strokeWidth={2} />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    style={{ display: "none" }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void handleExtractFromImage(file);
                      // Reset so picking the same file twice re-fires.
                      e.target.value = "";
                    }}
                  />
                </div>
                {extractError && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#F87171",
                      paddingLeft: 4,
                    }}
                  >
                    {extractError}
                  </div>
                )}
                {showSeedModeToggle && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      paddingLeft: 4,
                      fontSize: 11,
                      color: "var(--ql-fg-subtle)",
                    }}
                  >
                    <span style={{ fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>
                      Mode
                    </span>
                    {([
                      {
                        id: "faithful" as BetaSeedMode,
                        label: "Faithful",
                        title: "Keep your wording (~5% change max). Arabic gets a native rhythm; the English stays yours.",
                      },
                      {
                        id: "reinvent" as BetaSeedMode,
                        label: "Reinvent",
                        title: "Distil to the felt center. House voice rewrites the seed into a sharper aphorism.",
                      },
                    ]).map(({ id, label, title }) => {
                      const selected = seedMode === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setSeedMode(id)}
                          disabled={generating}
                          title={title}
                          style={{
                            padding: "4px 10px",
                            borderRadius: 999,
                            border: `1px solid ${selected ? "rgba(255,213,0,.30)" : "rgba(255,255,255,.08)"}`,
                            background: selected ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
                            color: selected ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: generating ? "wait" : "pointer",
                            fontFamily: "inherit",
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                    <span style={{ marginLeft: 4, opacity: 0.7 }}>
                      {seedMode === "faithful"
                        ? "Your wording, kept. Arabic gets a native rhythm."
                        : "Distilled to felt center. House-voice rewrite."}
                    </span>
                  </div>
                )}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch" }}>
              {/* Generate-target switch — what the button produces, decoupled
                  from the view filter above. Fixes the old "All view is
                  hardcoded to AI-image" behaviour: pick either pipeline here. */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: ".06em",
                    textTransform: "uppercase",
                    color: "var(--ql-fg-subtle)",
                  }}
                >
                  New post
                </span>
                <div
                  style={{
                    display: "inline-flex",
                    padding: 2,
                    gap: 2,
                    borderRadius: 999,
                    border: "1px solid rgba(255,255,255,.08)",
                    background: "rgba(255,255,255,.02)",
                  }}
                >
                  {([
                    { id: "beta" as const, label: "AI image" },
                    { id: "classic" as const, label: "Legacy" },
                  ]).map(({ id, label }) => {
                    const active = genPipeline === id;
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => selectGenPipeline(id)}
                        disabled={generating}
                        title={`Generate will create a ${id === "classic" ? "Legacy" : "AI image"} post`}
                        style={{
                          padding: "4px 12px",
                          borderRadius: 999,
                          border: `1px solid ${active ? "rgba(255,213,0,.30)" : "transparent"}`,
                          background: active ? "rgba(255,213,0,.12)" : "transparent",
                          color: active ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: generating ? "wait" : "pointer",
                          fontFamily: "inherit",
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleGenerate()}
                disabled={generating}
                title={
                  trimmedSeed
                    ? isQuoteSeed
                      ? `Build a ${genTarget} post around this quote/paragraph: "${trimmedSeed.slice(0, 80)}${trimmedSeed.length > 80 ? "…" : ""}"`
                      : `Generate a new ${genTarget} post seeded on: "${trimmedSeed}"`
                    : `Generate one ${genTarget} post now — picks the next theme from the rotation pool`
                }
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "10px 16px",
                  borderRadius: 12,
                  border: "1px solid rgba(255,213,0,.25)",
                  background: "rgba(255,213,0,.10)",
                  color: "var(--ql-yellow)",
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: generating ? "wait" : "pointer",
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                {generating ? (
                  <RefreshCw size={14} className="ql-spin" strokeWidth={2} />
                ) : (
                  <Sparkles size={14} strokeWidth={2} />
                )}
                {trimmedSeed
                  ? (isQuoteSeed ? `Build ${genTarget} from quote` : `Generate ${genTarget} from seed`)
                  : `Generate ${genTarget} post`}
              </button>
            </div>
          </div>
        }
      />

      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {(["all", "drafts", "scheduled", "published"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              padding: "8px 14px",
              borderRadius: 999,
              border: `1px solid ${tab === t ? "rgba(255,213,0,.30)" : "rgba(255,255,255,.08)"}`,
              background: tab === t ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
              color: tab === t ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              textTransform: "capitalize",
            }}
          >
            {t}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 20, alignItems: "center" }}>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: ".14em",
          color: "var(--ql-fg-subtle)", textTransform: "uppercase", marginRight: 4,
        }}>Pipeline</span>
        {([
          { id: "all" as Pipeline, label: "All" },
          { id: "beta" as Pipeline, label: "AI image" },
          { id: "classic" as Pipeline, label: "Legacy" },
        ]).map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => selectPipeline(id)}
            style={{
              padding: "6px 12px",
              borderRadius: 999,
              border: `1px solid ${pipeline === id ? "rgba(255,213,0,.30)" : "rgba(255,255,255,.08)"}`,
              background: pipeline === id ? "rgba(255,213,0,.10)" : "rgba(255,255,255,.02)",
              color: pipeline === id ? "var(--ql-yellow)" : "var(--ql-fg-muted)",
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Sort + date range (2026-09-14) — same control as Library Videos. The
          count on the right tells the operator when the range is hiding
          posts the tab would otherwise show. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 20, marginTop: -8 }}>
        <LibraryDateControls
          order={order}
          onOrder={setOrder}
          range={range}
          onRange={setRange}
          dateLabel="generated"
          compact={isMobile}
        />
        {!loading && hasDateRange(range) && (
          <span style={{ fontSize: 11.5, color: "var(--ql-fg-subtle)", fontFamily: "var(--ql-font-mono)", whiteSpace: "nowrap" }}>
            {viewCount} of {rawCount} in range
          </span>
        )}
      </div>

      {error && (
        <div
          role="alert"
          style={{
            margin: "0 0 18px",
            padding: "10px 14px",
            borderRadius: 12,
            background: "rgba(248,113,113,.10)",
            border: "1px solid rgba(248,113,113,.30)",
            color: "#FCA5A5",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            color: "var(--ql-fg-muted)",
            padding: "60px 20px",
            textAlign: "center",
          }}
        >
          Loading…
        </div>
      ) : rawCount > 0 && viewCount === 0 && !showClassicSkeleton ? (
        <div
          style={{
            padding: "60px 24px",
            textAlign: "center",
            border: "1px dashed rgba(255,255,255,.08)",
            borderRadius: 16,
            color: "var(--ql-fg-muted)",
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          <div style={{ marginBottom: 10, fontSize: 16, fontWeight: 700, color: "var(--ql-fg)" }}>
            No posts in this date range
          </div>
          {rawCount} {rawCount === 1 ? "post matches" : "posts match"} the current tab — widen or{" "}
          <button
            type="button"
            onClick={() => setRange({})}
            style={{
              background: "none", border: "none", padding: 0, cursor: "pointer",
              color: "var(--ql-yellow)", fontWeight: 700, fontFamily: "inherit", fontSize: 13,
            }}
          >
            clear the range
          </button>
          .
        </div>
      ) : rawCount === 0 && !showClassicSkeleton ? (
        <div
          style={{
            padding: "60px 24px",
            textAlign: "center",
            border: "1px dashed rgba(255,255,255,.08)",
            borderRadius: 16,
            color: "var(--ql-fg-muted)",
            fontSize: 13,
            lineHeight: 1.6,
          }}
        >
          <div
            style={{
              marginBottom: 10,
              fontSize: 16,
              fontWeight: 700,
              color: "var(--ql-fg)",
            }}
          >
            {tab === "drafts" ? "No drafts in the queue" : `No ${tab} posts yet`}
          </div>
          {tab === "drafts" || tab === "all"
            ? pipeline === "classic"
              ? "Click 'Generate one now' to create the first draft, or wait for the next cron tick (every 12 hours)."
              : pipeline === "all"
                ? "No posts yet from either pipeline (AI image or Legacy). Click 'Generate one now' to create the first draft."
                : "AI-image posts are atmospheric 4-slide carousels. Click 'Generate one now' to create the first draft."
            : "Posts will appear here once they're scheduled in Buffer."}
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          {pipeline === "all"
            ? (
              <>
                {showClassicSkeleton && classicSkeletonCard}
                {merged!.map((row) =>
                  row.kind === "classic" ? (
                    row.d.status === "generating-text" ? (
                      classicSkeleton(`c-${row.d.id}`)
                    ) : (
                      <PostCard
                        key={`c-${row.d.id}`}
                        draft={row.d}
                        lengthControl={lengthControl}
                        onChange={() => void fetchDrafts({ silent: true })}
                        onRecycled={handleRecycledClassic}
                        autoOpenPublish={autoPublishId === row.d.id}
                      />
                    )
                  ) : (
                    <BetaPostCard
                      key={`b-${row.d.id}`}
                      draft={row.d}
                      lengthControl={lengthControl}
                      onChange={() => void fetchDrafts({ silent: true })}
                      onRecycled={handleRecycledBeta}
                      autoOpenPublish={autoPublishId === row.d.id}
                    />
                  ),
                )}
              </>
            )
            : pipeline === "classic"
            ? (
              <>
                {showClassicSkeleton && classicSkeletonCard}
                {viewDrafts.map((d) =>
                  d.status === "generating-text" ? (
                    classicSkeleton(`c-${d.id}`)
                  ) : (
                    <PostCard
                      key={d.id}
                      draft={d}
                      lengthControl={lengthControl}
                      onChange={() => void fetchDrafts({ silent: true })}
                      onRecycled={handleRecycledClassic}
                      autoOpenPublish={autoPublishId === d.id}
                    />
                  ),
                )}
              </>
            )
            : viewBeta.map((d) => (
                <BetaPostCard
                  key={d.id}
                  draft={d}
                  lengthControl={lengthControl}
                  onRecycled={handleRecycledBeta}
                  autoOpenPublish={autoPublishId === d.id}
                  // Silent — per-card actions surface their own busy state
                  // on the originating card. Triggering a page-level
                  // loading flip would unmount the grid and wipe every
                  // other card's open Edit modal / History panel / unsaved
                  // textarea. See the polling effect above for the same
                  // pattern.
                  onChange={() => void fetchDrafts({ silent: true })}
                />
              ))}
        </div>
      )}
    </div>
  );
}
